import { useId, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';

import { taskStateWords, type BoardLoad } from '@/features/tasks';
import { useCanWork } from '@/features/workspaces/canWork';
import { PANEL } from '@/features/workspace-overview/stateStyles';
import { SECTION_HEADING } from '@/shared/components/sectionHeading';
import { WAITING_ON_SUBTASKS_FILL } from '@/shared/components/stateFills';

export interface BoardsSectionProps {
  workspaceId: string;
  /** Published workflows whose board built, heaviest first. */
  boards: BoardLoad[];
}

/**
 * The boards of this workspace, as a way in — and as a statement of which one
 * to walk into first.
 *
 * Deliberately not the workflow list, which is a built page of its own with
 * search, filters and the controls that create workflows. This lists *boards
 * you can enter*; that page lists *workflows you can manage*. Which is why a
 * draft appears here not at all — it has no board, so a draft row would be a
 * row nothing happens when you click. A workflow whose board will not build is
 * absent for the same reason, and named instead by the notice above, which
 * offers its builder.
 *
 * Boards holding nothing fold into one line, "4 boards with no tasks" (spec
 * 153 FR-014), which opens to list them. An earlier fold was reverted because
 * it removed the only way to reach those boards from this page; this one
 * opens to the same links, so every board stays one press further at most.
 *
 * Names are shown in full and wrap: the name is the reason the list exists,
 * and "Design re…" beside "Design sy…" told the reader nothing.
 */
export function BoardsSection({ workspaceId, boards }: BoardsSectionProps) {
  if (boards.length === 0) return null;

  const loaded = boards.filter((board) => board.total > 0);
  const empty = boards.filter((board) => board.total === 0);
  // Floored at one, so a segment's width is never `NaN%`.
  const busiest = Math.max(1, ...loaded.map((board) => board.total));

  return (
    <section
      aria-labelledby="overview-boards"
      className="flex flex-col gap-2.5"
    >
      <div className="flex items-baseline gap-3">
        <h2 id="overview-boards" className={SECTION_HEADING}>
          Boards
        </h2>
        {/* Said, because an order nobody can see is an order nobody trusts —
            and because "the first one is the worst one" is the only reason to
            read this section top-down rather than hunting for a name. */}
        <p className="text-muted-foreground ms-auto text-xs">Worst first</p>
      </div>

      <div className={PANEL}>
        <ul>
          {loaded.map((board) => (
            <li key={board.workflowKey}>
              <Link
                to="/workspaces/$workspaceId/workflows/$workflowKey/board"
                params={{ workspaceId, workflowKey: board.workflowKey }}
                className="hover:bg-accent focus-visible:ring-ring border-border block border-t px-3.5 py-2 transition-colors first:border-t-0 focus-visible:ring-inset focus-visible:ring-3 focus-visible:outline-none motion-safe:duration-[var(--motion-duration-base)]"
              >
                {/* The tally drops under the name rather than squeezing it: a
                    name broken over two lines ("Design / review") reads as
                    two boards. */}
                <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className="text-foreground min-w-0 text-xs font-medium break-words">
                    {board.workflowName}
                  </span>
                  <span className="text-muted-foreground ms-auto shrink-0 text-xs tabular-nums">
                    <BoardTally board={board} />
                  </span>
                </span>
                <span className="mt-1.5 flex items-center gap-2">
                  <LoadBar board={board} busiest={busiest} />
                  <StepStrip board={board} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
        {empty.length > 0 ? (
          <EmptyBoards
            workspaceId={workspaceId}
            boards={empty}
            alone={loaded.length === 0}
          />
        ) : null}
      </div>
    </section>
  );
}

/** The boards with no tasks, folded into one line that opens to them. */
function EmptyBoards({
  workspaceId,
  boards,
  alone,
}: {
  workspaceId: string;
  boards: BoardLoad[];
  /** No loaded board above it, so no rule between them. */
  alone: boolean;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const count = boards.length;
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <div className={alone ? '' : 'border-border border-t'}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          setOpen(!open);
        }}
        className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring flex w-full items-center gap-1.5 px-3.5 py-2 text-start text-xs transition-colors focus-visible:ring-3 focus-visible:outline-none focus-visible:ring-inset pointer-coarse:min-h-11"
      >
        <Chevron aria-hidden="true" className="size-3.5 shrink-0" />
        {`${String(count)} ${count === 1 ? 'board' : 'boards'} with no tasks`}
      </button>
      <ul id={listId} hidden={!open} className="pb-1.5">
        {boards.map((board) => (
          <li key={board.workflowKey}>
            <Link
              to="/workspaces/$workspaceId/workflows/$workflowKey/board"
              params={{ workspaceId, workflowKey: board.workflowKey }}
              className="text-foreground hover:bg-accent focus-visible:ring-ring block px-3.5 py-1.5 ps-8 text-xs break-words transition-colors focus-visible:ring-3 focus-visible:outline-none focus-visible:ring-inset"
            >
              {board.workflowName}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * What this board is holding, as one bar on the shared scale.
 *
 * The track is the scale made visible: without it a bar is a floating dash that
 * says only "some", and the shared denominator — the busiest board — exists
 * nowhere on screen. With it, every row reads as a fraction of the same whole,
 * which is the comparison the column is for.
 *
 * Purely redundant with the tally above it, and that is the point: it exists so
 * the column can be *compared* without being read. `aria-hidden` for the same
 * reason — a screen reader gets the tally, which is the same fact in better
 * words, and announcing both would be announcing it twice.
 *
 * Segment order matches the composition bar's: brand blue and brand purple
 * cannot be told apart as adjacent fills, so waiting and running never touch.
 */
function LoadBar({ board, busiest }: { board: BoardLoad; busiest: number }) {
  const width = (count: number) => `${String((count / busiest) * 100)}%`;

  return (
    <span
      aria-hidden="true"
      className="bg-muted-foreground/10 flex h-1.5 w-14 shrink-0 items-center gap-0.5 overflow-hidden rounded-full"
    >
      <Segment width={width(board.waiting)} fill="bg-board-state-waiting" />
      <Segment width={width(board.broken)} fill="bg-board-state-failed" />
      <Segment width={width(board.running)} fill="bg-board-state-running" />
      <Segment
        width={width(board.waitingOnSubtasks)}
        fill={WAITING_ON_SUBTASKS_FILL}
      />
      <Segment width={width(board.atRest)} fill="bg-muted-foreground/30" />
    </span>
  );
}

function Segment({ width, fill }: { width: string; fill: string }) {
  if (width === '0%') return null;
  return <span className={`${fill} h-full rounded-full`} style={{ width }} />;
}

/**
 * Where along the board those tasks are standing.
 *
 * One mark per step, in board order, filled in proportion to how many tasks
 * that step is holding. It answers a question the state bar cannot — *four
 * broken* and *four broken all piled on one step* are the same bar and very
 * different problems — and it is the board's own column counts, so it cannot
 * describe a shape the board would draw differently.
 *
 * Deliberately not labelled: at this size a step name would not fit, and the
 * strip is a shape to recognise rather than a chart to read. The `title` names
 * the steps for a pointer, and the tally above carries the numbers.
 *
 * Neutral ink, never a state colour. Colour on this page means *what a task is
 * doing*, and a strip that drew step occupancy in the running blue said four
 * tasks were running when none of them were. Position is what this mark
 * encodes, so height is what it varies; the bar beside it owns colour.
 *
 * Narrower than the load bar, too. It is the subordinate of the pair — "how
 * much trouble" is the question this list is sorted by, and "where along the
 * board" only refines it.
 */
function StepStrip({ board }: { board: BoardLoad }) {
  // Two steps is a strip with nothing to say about distribution, and a board
  // whose steps are unreadable has none at all.
  if (board.steps.length < 3) return null;

  const busiestStep = Math.max(1, ...board.steps.map((step) => step.count));

  return (
    <span
      aria-hidden="true"
      title={board.steps
        .map((step) => `${step.label}: ${String(step.count)}`)
        .join('\n')}
      className="flex h-2 w-10 shrink-0 items-end gap-px"
    >
      {board.steps.map((step) => (
        <span
          key={step.stepId}
          className={`min-w-0 flex-1 rounded-[1px] ${
            step.count > 0 ? 'bg-muted-foreground/45' : 'bg-muted-foreground/15'
          }`}
          // Height carries the count; an empty step keeps a floor so the strip
          // still reads as a pipeline rather than as gaps between marks.
          style={{
            height:
              step.count > 0
                ? `${String(35 + (step.count / busiestStep) * 65)}%`
                : '18%',
          }}
        />
      ))}
    </span>
  );
}

/**
 * The board's contents in words, worst first, and only the states it has.
 *
 * Zeroes are omitted rather than printed. A row reading "0 need attention ·
 * 0 waiting on you · 0 running · 3 at rest" spends four facts to deliver one,
 * and a column of those trains the eye to skip the place where the real
 * signals appear.
 */
function BoardTally({ board }: { board: BoardLoad }) {
  // A reader cannot answer a gate: it waits on a member (spec 193).
  const waiting = taskStateWords(useCanWork()).waiting;
  const parts: string[] = [];
  // The overview's four states, in its words (spec 150): a parent waiting on
  // its subtasks is never "running".
  if (board.broken > 0)
    parts.push(
      `${String(board.broken)} ${board.broken === 1 ? 'needs' : 'need'} attention`,
    );
  if (board.waiting > 0) parts.push(waiting.counted(board.waiting));
  if (board.running > 0) parts.push(`${String(board.running)} running`);
  if (board.waitingOnSubtasks > 0)
    parts.push(`${String(board.waitingOnSubtasks)} waiting on subtasks`);
  if (parts.length === 0) return <>{board.atRest} at rest</>;

  return <>{parts.join(' · ')}</>;
}
